// The fitted grid (slice 07, issue 747; design/09-fitting-the-grid.md,
// history/04-mvp-epic.md §7.3, plan §2.6).
//
// Two independent axes. Each names the edge its lines count from, a base
// pitch, and its ANCHORS — the locked lines, `{index, pos}` — from which
// every other line is derived: evenly between two anchors, at the last
// pitch beyond them. A uniform grid is the instance with one anchor and
// equal gaps; nothing here special-cases it. A drag of an unlocked line is
// PROVISIONAL: it sits in the axis as `provisional` so the cascade it causes
// is what everyone sees on release, and it is recomputed away the moment a
// different line is locked (design/09 §4 — lock as you go).
//
// Pure: no rendering, no rounding beyond what the arithmetic gives. What
// goes in comes out the same, which is what lets a grid survive save and
// load byte-identically.
/** Whether the grid is switched off (issue 1035, GRID-005). */
export const isGridless = (g) => g.mode === 'none';
/**
 * The pieces of a grid line drawn round the ungridded areas (issue 1035): a vertical line ('x') at board x `at`, or a
 * horizontal one ('y') at board y `at`, running 0..`length`. Each area the line passes strictly through cuts it.
 */
export function lineSegments(axis, at, length, blanks) {
    const cuts = blanks
        .filter((b) => (axis === 'x' ? at > b.x && at < b.x + b.width : at > b.y && at < b.y + b.height))
        .map((b) => (axis === 'x' ? [b.y, b.y + b.height] : [b.x, b.x + b.width]))
        .sort((p, q) => p[0] - q[0]);
    const out = [];
    let from = 0;
    for (const [a, b] of cuts) {
        if (a > from)
            out.push([from, Math.min(a, length)]);
        from = Math.max(from, b);
        if (from >= length)
            break;
    }
    if (from < length)
        out.push([from, length]);
    return out.filter(([a, b]) => b > a);
}
export const GRID_LIMITS = { minPitch: 4, maxPitch: 4000, maxLines: 400, maxAnchors: 400, maxBlank: 20 };
export const DEFAULT_APPEARANCE = { colour: '#78716c', opacity: 0.6, visible: true };
/** The grid a table starts with: 70 px squares from the left and the top (plan §2.10). */
export function defaultGrid(pitch = 70) {
    return {
        x: { edge: 'left', basePitch: pitch, anchors: [{ index: 0, pos: 0 }], provisional: null },
        y: { edge: 'top', basePitch: pitch, anchors: [{ index: 0, pos: 0 }], provisional: null },
        appearance: { ...DEFAULT_APPEARANCE },
    };
}
/** Setup by squares across: the pitch is the extent over the count, lines fanning from the edge. */
export function axisBySquares(edge, squares, extent) {
    return { edge, basePitch: extent / squares, anchors: [{ index: 0, pos: 0 }], provisional: null };
}
/** Setup by square size. */
export function axisByPitch(edge, pitch) {
    return { edge, basePitch: pitch, anchors: [{ index: 0, pos: 0 }], provisional: null };
}
/** The anchors the derivation uses: the locked ones, plus the provisional line as if locked. */
function effectiveAnchors(axis) {
    const all = axis.provisional && !axis.anchors.some((a) => a.index === axis.provisional.index)
        ? [...axis.anchors, axis.provisional]
        : axis.anchors;
    return [...all].sort((a, b) => a.index - b.index);
}
/**
 * Every line of an axis across an extent, by history/04 §7.3's three rules:
 * evenly between consecutive anchors; beyond the last at the pitch the last
 * two set (or the base pitch with one anchor); before the first, the mirror,
 * while the position stays past the edge.
 */
export function deriveAxis(axis, extent) {
    const anchors = effectiveAnchors(axis);
    if (anchors.length === 0 || !(extent > 0))
        return [];
    const lockedAt = new Set(axis.anchors.map((a) => a.index));
    const provisionalAt = axis.provisional && !lockedAt.has(axis.provisional.index) ? axis.provisional.index : null;
    const mark = (index, pos) => ({ index, pos, locked: lockedAt.has(index), provisional: index === provisionalAt });
    const lines = [];
    const first = anchors[0];
    const last = anchors[anchors.length - 1];
    const pitchAfter = anchors.length >= 2
        ? (last.pos - anchors[anchors.length - 2].pos) / (last.index - anchors[anchors.length - 2].index)
        : axis.basePitch;
    const pitchBefore = anchors.length >= 2
        ? (anchors[1].pos - first.pos) / (anchors[1].index - first.index)
        : axis.basePitch;
    if (!(pitchAfter > 0) || !(pitchBefore > 0))
        return anchors.map((a) => mark(a.index, a.pos));
    // Before the first anchor: back towards the edge, down to index 0 — the
    // first line by definition — while the line is still past the edge.
    for (let k = first.index - 1; k >= 0 && lines.length < GRID_LIMITS.maxLines; k -= 1) {
        const pos = first.pos - pitchBefore * (first.index - k);
        if (pos <= 0)
            break;
        lines.unshift(mark(k, pos));
    }
    // Between consecutive anchors: evenly.
    for (let a = 0; a < anchors.length - 1; a += 1) {
        const A = anchors[a];
        const B = anchors[a + 1];
        lines.push(mark(A.index, A.pos));
        for (let k = A.index + 1; k < B.index; k += 1) {
            lines.push(mark(k, A.pos + ((B.pos - A.pos) * (k - A.index)) / (B.index - A.index)));
        }
    }
    lines.push(mark(last.index, last.pos));
    // Past the last anchor: at the last pitch, until past the far edge.
    for (let k = last.index + 1; lines.length < GRID_LIMITS.maxLines; k += 1) {
        const pos = last.pos + pitchAfter * (k - last.index);
        if (pos > extent)
            break;
        lines.push(mark(k, pos));
    }
    return lines.filter((l) => l.pos >= 0 && l.pos <= extent);
}
/** A position from the axis's edge as a board coordinate (the board counts from the left and the top). */
export function toBoard(edge, pos, extent) {
    return edge === 'left' || edge === 'top' ? pos : extent - pos;
}
export function fromBoard(edge, coord, extent) {
    return edge === 'left' || edge === 'top' ? coord : extent - coord;
}
const sorted = (anchors) => [...anchors].sort((a, b) => a.index - b.index);
/** Lock a line at a position: an anchor at that index, replacing any there; a provisional line elsewhere is recomputed away. */
export function lockLine(axis, index, pos) {
    const anchors = sorted([...axis.anchors.filter((a) => a.index !== index), { index, pos }]);
    return { ...axis, anchors, provisional: null };
}
/** Unlock a line: it returns to its derived position. The last anchor stays — at least one is always required. */
export function unlockLine(axis, index) {
    if (axis.anchors.length <= 1 || !axis.anchors.some((a) => a.index === index))
        return axis;
    return { ...axis, anchors: axis.anchors.filter((a) => a.index !== index) };
}
/** A drag of an unlocked line: provisional until locked, and gone the moment another line is locked. */
export function dragLine(axis, index, pos) {
    if (axis.anchors.some((a) => a.index === index))
        return axis; // locked means genuinely immovable
    return { ...axis, provisional: { index, pos } };
}
/** Lock the provisional line where it is. */
export function lockProvisional(axis) {
    return axis.provisional ? lockLine(axis, axis.provisional.index, axis.provisional.pos) : axis;
}
/** Back to lines fanning evenly from the edge at the base pitch. */
export function resetAxis(axis) {
    return { ...axis, anchors: [{ index: 0, pos: 0 }], provisional: null };
}
const EDGES = ['left', 'right', 'top', 'bottom'];
const finite = (v) => typeof v === 'number' && Number.isFinite(v);
function parseAnchor(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { index, pos } = v;
    if (!Number.isInteger(index) || Math.abs(index) > GRID_LIMITS.maxLines)
        return null;
    if (!finite(pos) || Math.abs(pos) > 1e6)
        return null;
    return { index: index, pos };
}
function parseAxis(v, vertical) {
    if (typeof v !== 'object' || v === null)
        return null;
    const a = v;
    const edges = vertical ? ['left', 'right'] : ['top', 'bottom'];
    if (typeof a.edge !== 'string' || !edges.includes(a.edge))
        return null;
    if (!finite(a.basePitch) || a.basePitch < GRID_LIMITS.minPitch || a.basePitch > GRID_LIMITS.maxPitch)
        return null;
    if (!Array.isArray(a.anchors) || a.anchors.length === 0 || a.anchors.length > GRID_LIMITS.maxAnchors)
        return null;
    const anchors = [];
    for (const x of a.anchors) {
        const anchor = parseAnchor(x);
        if (!anchor || anchors.some((y) => y.index === anchor.index))
            return null;
        anchors.push(anchor);
    }
    const provisional = a.provisional === null || a.provisional === undefined ? null : parseAnchor(a.provisional);
    if (a.provisional !== null && a.provisional !== undefined && !provisional)
        return null;
    return { edge: a.edge, basePitch: a.basePitch, anchors: sorted(anchors), provisional };
}
/** A grid from the wire, exact or null. */
export function parseGrid(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const g = value;
    const x = parseAxis(g.x, true);
    const y = parseAxis(g.y, false);
    if (!x || !y)
        return null;
    if (typeof g.appearance !== 'object' || g.appearance === null)
        return null;
    const ap = g.appearance;
    if (typeof ap.colour !== 'string' || !/^#[0-9a-f]{6}$/.test(ap.colour))
        return null;
    if (!finite(ap.opacity) || ap.opacity < 0 || ap.opacity > 1)
        return null;
    if (typeof ap.visible !== 'boolean')
        return null;
    // Issue 1035: both optional, and written back only when present.
    if (g.mode !== undefined && g.mode !== 'none')
        return null;
    let blank;
    if (g.blank !== undefined) {
        if (!Array.isArray(g.blank) || g.blank.length > GRID_LIMITS.maxBlank)
            return null;
        blank = [];
        for (const r of g.blank) {
            if (typeof r !== 'object' || r === null)
                return null;
            const { x: bx, y: by, width, height } = r;
            if (![bx, by, width, height].every((n) => finite(n) && n >= 0 && n <= 1e6) || !(width > 0) || !(height > 0))
                return null;
            blank.push({ x: bx, y: by, width: width, height: height });
        }
    }
    return { x, y, appearance: { colour: ap.colour, opacity: ap.opacity, visible: ap.visible }, ...(g.mode === 'none' ? { mode: 'none' } : {}), ...(blank ? { blank } : {}) };
}
export const isEdge = (v) => typeof v === 'string' && EDGES.includes(v);
/** An area left ungridded from a drag between two board points (issue 1035): held on the board; under 4 px a side is none. */
export function blankArea(a, b, extent) {
    const clamp = (v, hi) => Math.max(0, Math.min(hi, v));
    const x0 = clamp(Math.min(a.x, b.x), extent.width);
    const x1 = clamp(Math.max(a.x, b.x), extent.width);
    const y0 = clamp(Math.min(a.y, b.y), extent.height);
    const y1 = clamp(Math.max(a.y, b.y), extent.height);
    return x1 - x0 >= 4 && y1 - y0 >= 4 ? { x: x0, y: y0, width: x1 - x0, height: y1 - y0 } : null;
}
//# sourceMappingURL=grid.js.map